'use client';

import { useState } from 'react';
import { signIn, resetPassword, confirmResetPassword, SignInInput, confirmSignIn } from 'aws-amplify/auth';

export default function EmailPasswordSignIn() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'signIn' | 'newPassword' | 'forgotPassword' | 'resetPassword'>('signIn');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const input: SignInInput = { username: username.toLowerCase(), password };
      const result = await signIn(input);
      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setStep('newPassword');
        setMessage('You must set a new password.');
        return;
      }
      setMessage('Signed in successfully!');
    } catch (error: unknown) {
      if (error instanceof Error) {
        setMessage(error.message);
      } else {
        setMessage('Error signing in.');
      }
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      setStep('signIn');
      setMessage('Password updated and signed in!');
    } catch (error: unknown) {
      setMessage(error instanceof Error ? error.message : 'Error confirming new password.');
    }
  };

  const handleForgotPassword = async () => {
    try {
      await resetPassword({ username: username.toLowerCase() });
      setStep('resetPassword');
      setMessage('Check your email for the confirmation code.');
    } catch (error: unknown) {
      if (error instanceof Error) {
        setMessage(error.message);
      } else {
        setMessage('Error sending reset code.');
      }
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await confirmResetPassword({
        username: username.toLowerCase(),
        newPassword,
        confirmationCode
      });
      setStep('signIn');
      setMessage('Password reset successfully! You can now sign in.');
    } catch (error: unknown) {
      if (error instanceof Error) {
        setMessage(error.message);
      } else {
        setMessage('Error resetting password.');
      }
    }
  };

  return (
    <div className="flex flex-col w-80 p-6 bg-white shadow-md rounded-lg border border-gray-200">
      {step === 'signIn' && (
        <form onSubmit={handleSignIn} className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold mb-2">Sign In</h2>

          <input
            type="email"
            placeholder="Email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            required
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            required
          />

          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg"
          >
            Sign In
          </button>

          <button
            type="button"
            onClick={() => setStep('forgotPassword')}
            className="text-sm text-blue-500 hover:underline mt-2"
          >
            Forgot Password?
          </button>
        </form>
      )}

      {step === 'newPassword' && (
        <form onSubmit={handleNewPasswordSubmit} className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold">Set New Password</h2>
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            required
          />
          <button
            type="submit"
            className="bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg"
          >
            Update Password
          </button>
        </form>
      )}

      {step === 'forgotPassword' && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold">Forgot Password</h2>
          <input
            type="email"
            placeholder="Email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button
            onClick={handleForgotPassword}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg"
          >
            Send Reset Code
          </button>
          <button
            type="button"
            onClick={() => setStep('signIn')}
            className="text-sm text-gray-500 hover:underline mt-2"
          >
            Back to Sign In
          </button>
        </div>
      )}

      {step === 'resetPassword' && (
        <form onSubmit={handleResetPassword} className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold">Reset Password</h2>
          <input
            type="text"
            placeholder="Confirmation Code"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
            className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button
            type="submit"
            className="bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg"
          >
            Reset Password
          </button>
        </form>
      )}

      {message && <p className="text-sm text-center text-gray-600 mt-4">{message}</p>}
    </div>
  );
}
